import * as React from "react";
import { Appearance } from "common/generator/components/button-appearance";
import tw from "twin.macro";
import { useRuntimeTheme } from "hooks/use-runtime-theme";
import chroma from "chroma-js";
import { SectionStyle, useSectionStyle } from "../wire-container/wire-container";
import { styled } from "webapp/styled-components";
import classNames from "classnames";

interface Props extends React.DetailedHTMLProps<React.ButtonHTMLAttributes<HTMLButtonElement>, HTMLButtonElement> {
    readonly appearance: Appearance;
    readonly size?: "sm" | "md" | "lg";
}

const WireButtonStyle = styled.button`
    &.sm {
        padding: 8px 18px;
    }

    &.md {
        padding: 10px 20px;
        ${tw`text-base`}
    }

    &.lg {
        padding: 14px 28px;
        ${tw`text-base`}
    }
`;

export const WireButton: React.FC<Props> = p => {
    const { appearance, children, size = "sm", ...rest } = p;

    const theme = useRuntimeTheme();

    let style: React.CSSProperties | undefined;
    if (appearance === Appearance.Transparent) {
        style = {
            backgroundColor: chroma(theme.accent).alpha(0.2).css(),
        };
    }

    const sectionStyle = useSectionStyle();

    const isAccent = sectionStyle === SectionStyle.Accent;
    const f = appearance === Appearance.Filled;
    const b = appearance === Appearance.Bordered;
    const simple = appearance === Appearance.Simple;
    const transparent = appearance === Appearance.Transparent;

    const filledAccent = f && isAccent;
    const filled = f && !isAccent;
    const borderedAccent = b && isAccent;
    const boredered = b && !isAccent;

    const button = (
        <WireButtonStyle
            {...(rest as any)}
            className={classNames(rest.className, size)}
            style={{ ...rest.style, ...style }}
            css={[
                tw`relative disabled:(opacity-40 pointer-events-none) after:(content inset-0 absolute pointer-events-none
                rounded-lg transition-shadow) text-sm font-semibold transition-all rounded-lg text-text-base`,
                filledAccent &&
                    tw`text-accent bg-white shadow-md-dark hover:(shadow-md-xdark background[rgba(255, 255, 255, .85)])
                    focus-visible:(background[rgba(255, 255, 255, .85)] after:(ring-2 ring-white))`,
                filled &&
                    tw`color[white] bg-accent filter hover:brightness-95 focus-visible:after:(ring-2 ring-accent -inset-px
                    border-radius[9px])`,
                borderedAccent &&
                    tw`color[white] background-color[rgba(255, 255, 255, .25)] hover:(background-color[rgba(255, 255, 255,
                    .17)]) focus-visible:after:(ring-2 ring-white)`,
                boredered &&
                    tw`text-text-base shadow-md-dark hover:(text-text-dark shadow-md-xdark) bg-white
                    focus-visible:after:(ring-2 ring-accent)`,
                simple && tw`text-accent`,
                transparent && tw`text-accent`,
            ]}>
            {children}
        </WireButtonStyle>
    );
    return button;
};
